/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'
import {isHydrated, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {
    Box,
    Image,
    Flex,
    Text,
    Button,
    VStack,
    Skeleton,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'

// project components
import ImageGallery from '../image-gallery'
import Breadcrumb from '../breadcrumb'
import withRegistration from '../with-registration'
import {Skeleton as ImageGallerySkeleton} from '../image-gallery'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    getItemBadge,
    getPriceData,
    getSetPriceData,
    isLimitReached,
    filterImageBadges,
    isCharmBadge
} from '../../utils/product-utils'
import WishlistAnimation from '../product-tile/partials/wishlist-animation'
import ProductDetailsAccordion from './partials/product-details-accordion'
import ProductPromotionCallouts from './partials/product-promotion-callouts'
import PDButton from '../purple-dot/pd-button'

import {getUrl} from '../../utils/cloudinary-utils'
import SizeGuide from './partials/size-guide'
import StickyBar from './partials/sticky-bar'
import LoadingSpinner from '../loading-spinner'
import CompleteTheLook from './partials/complete-the-look'
import BreadcrumbScemaMarkup from './partials/breadcrumbs-scema-markup'
import Callout from './partials/callout'
import ProductNotifyForm from './partials/product-notify-form'
import AttentiveBackInStock from './partials/attentive-back-in-stock'
import {isAttentiveSubscriptionEnabled} from '../../utils/email-subscription-provider'
import MirelzButtons from './partials/mirelz-btns'
import BNPLMessages from './partials/bnpl-messages'
import StoreAvailability from './partials/store-availability'
import useTagManager from '../../hooks/use-tag-manager'
import Engravable from '../engravable'
import {useGlobaleContext} from '../../contexts/globale'
import GWPMeter from '../promo-callout/gwp'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {useAddToCartDrawerContext} from '../../hooks/use-add-to-cart-drawer'
import useMonetate from '../../hooks/use-monetate'
import {useSiteContext} from '../../contexts/site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    useAccessToken,
    useCommerceApi,
    usePromotions,
    useProducts
} from '@salesforce/commerce-sdk-react'
import BMSMMeter from '../../components/promo-callout/bmsm'
import {getBasketProductIds, getQualifyingProductIds} from '../../utils/basket-utils'
import ComboChildProducts from '../combo-child-products'
import DiamondExpertSection from './partials/diamond-expert-section'
import QuantityLimit from './partials/quantity-limit'
import QuantityPicker from '../quantity-picker'
import ProductViewSwatches from './partials/product-view-swatches'
import {ProductViewHeader} from './partials/product-view-header'
import {formattedURL} from '../../utils/url'
import XxValueBadgeDisplay from '../product-tile/partials/xx-value-badge-display'
import {scheduleIdleAnalytics, yieldToMain} from '../../utils/common-utils'

const LARGE = 'large'
const COOKIE_ID = 'gwp_promotion'

const ButtonWithRegistration = withRegistration(Button)

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            product: productOriginal,
            imageSize = 'md',
            addToCart,
            wishlist = {},
            updateCart,
            isEngagementProduct = false,
            isSet = false,
            basket,
            comboProduct = null,
            dataLayerLoaded = true,
            countryISO,
            addToWishlist,
            isQuickAddModal = false,
            onModalClose,
            updateWishlist,
            isItemInWishlist,
            productAddedToWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            selectedBundleParentQuantity = 1,
            analyticsProps = {},
            pwpTargetPromotionId,
            globalePrices,
            isCart = false,
            suppressSwatchSpinner = false
        },
        ref
    ) => {
        const {currencyCode} = useGlobaleContext()
        const {currency: activeCurrency} = useCurrency()
        const api = useCommerceApi()
        const {getTokenWhenReady} = useAccessToken()
        const [childProductSelection, setChildProductSelection] = useState({})
        const [isChildLoading, setIsChildLoading] = useState(false)
        const isAllProductsSelected =
            Object.keys(childProductSelection)?.length === comboProduct?.childProducts?.length
        const isAnyChildNotOrderable = !!Object.values(childProductSelection).find(
            (p) => !p.selectedSku?.orderable
        )
        const {onOpen: onAddToCartDrawerOpen, onClose: onAddToCartDrawerClose} =
            useAddToCartDrawerContext()
        const [showPriceSkeleton, setShowPriceSkeleton] = useState(true)
        const {globaleCurrency} = useGlobaleContext()
        const [product, setProduct] = useState(productOriginal)
        const [shouldFetchProduct, setShouldFetchProduct] = useState(false)
        useEffect(() => {
            const fetchVariant = async () => {
                if (!shouldFetchProduct) return

                const token = await getTokenWhenReady()
                const requestParams = {
                    id: product?.productId,
                    perPricebook: true,
                    allImages: true,
                    c_selectedCountry: countryISO,
                    c_selectedCurrency: currencyCode,
                    expand: [
                        'availability',
                        'promotions',
                        'options',
                        'images',
                        'prices',
                        'variations'
                    ]
                }

                if (countryISO && globaleCurrency) {
                    requestParams.c_selectedCountry = countryISO
                    requestParams.c_selectedCurrency = globaleCurrency
                }
                setShowPriceSkeleton(true)
                try {
                    const variantResponse = await api.shopperProducts.getProduct({
                        parameters: requestParams,
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
                    setProduct(variantResponse)
                    setShouldFetchProduct(false)
                    setShowPriceSkeleton(false)
                    if (localStorage.getItem('changingSwatch') === 'true') {
                        var breadCrumb = variantResponse?.c_breadcrumbs
                            ? variantResponse?.c_breadcrumbs?.map((b) => b.htmlValue).join('/') +
                              '/'
                            : ''
                        breadCrumb = 'Home/' + breadCrumb + variantResponse.name
                        const category = {
                            id: variantResponse?.c_primaryCategory
                        }
                        tagManager.productClick(
                            'pdp',
                            variantResponse,
                            variantResponse,
                            undefined,
                            breadCrumb,
                            category,
                            'pdp',
                            undefined
                        )
                        localStorage.removeItem('changingSwatch')
                    }
                } catch (e) {
                    setShouldFetchProduct(false)
                    setShowPriceSkeleton(false)
                }
                return
            }

            fetchVariant()
        }, [shouldFetchProduct])

        const location = useLocation()
        const monetate = useMonetate()
        const {data: customer} = useCurrentCustomer()
        const intl = useIntl()
        const styles = useStyleConfig('ProductView')
        const tagManager = useTagManager()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const [showAddToCartLoader, setShowAddToCartLoader] = useState(false)
        const [driveRecipeID, setDriveRecipeID] = useState(null)
        const [engravingChecksum, setEngravingChecksum] = useState(null)
        const [engraveSwatchSpinner, setEngraveSwatchSpinner] = useState(null)
        const [openEngraving, setOpenEngraving] = useState(false)
        const [engravingGtmObject, setEngravingGtmObject] = useState(null)
        const [mobileImageIndex, setMobileImageIndex] = useState(0)
        const [showAddedCta, setShowAddedCta] = useState(false)
        const {isYR, isKS, setOpenBonusModal} = useSiteContext()
        const [selectedCarat, setSelectedCarat] = useState(false)
        const {
            app: {commerceAPI}
        } = getConfig()

        const {purpleDot, engraving, attentive} = commerceAPI
        const {config} = purpleDot || {}
        const {config: engravingConfig} = engraving || {}
        const {config: attentiveConfig} = attentive || {}
        var enableEngraving = false
        var enablePurpleDot = false
        const useAttentiveBackInStock = isAttentiveSubscriptionEnabled(attentiveConfig)

        try {
            if (config) {
                enablePurpleDot = JSON.parse(config).enabled
            }
        } catch (e) {
            console.log(e.message)
        }
        try {
            enableEngraving = JSON.parse(engravingConfig).enableEngraving
        } catch (e) {
            console.log(e.message)
        }

        const {affirm, paypal, afterpay} = commerceAPI
        const {configAffirm} = affirm || {}
        const {configPaypal} = paypal || {}
        const {configAfterpay} = afterpay || {}

        //paypal
        var paypalMinTotal = 50.0
        var enablePayPalPDPCallout = false
        var paypalScriptUrl = null

        //affirm
        var affirmMinTotal = 50.0
        var enableAffirmPDPCallout = false
        var affirmScriptUrl = null
        var affirmPublicKey = null

        //afterpay
        var enableAfterpayPDPCallout = false
        var afterpayScriptUrl = null
        var apPDPPlacementId = null
        var apPDPMpid = null

        const limitReached = isLimitReached(product?.id, basket)
        try {
            const affirmJsonConfig = JSON.parse(configAffirm)
            const afterpayJsonConfig = JSON.parse(configAfterpay)
            const paypalJsonConfig = JSON.parse(configPaypal)

            paypalMinTotal = paypalJsonConfig.minTotal
            enablePayPalPDPCallout = paypalJsonConfig.enablePDPCallout
            paypalScriptUrl = paypalJsonConfig.scriptURL

            //affirm
            affirmMinTotal = affirmJsonConfig.minTotal
            enableAffirmPDPCallout = affirmJsonConfig.enablePDPCallout
            affirmScriptUrl = affirmJsonConfig.scriptURL
            affirmPublicKey = affirmJsonConfig.apiKey

            //afterpay
            enableAfterpayPDPCallout = afterpayJsonConfig.enablePDPCallout
            afterpayScriptUrl = afterpayJsonConfig.scriptURL
            apPDPPlacementId = afterpayJsonConfig.pdpPlacementID
            apPDPMpid = afterpayJsonConfig.pdpMPID
        } catch (e) {
            console.log(e.message)
        }

        const {storefrontPreferences} = commerceAPI.storefront || {}
        let enableYRBOPISPDP = false

        try {
            if (storefrontPreferences) {
                const customPrefsAsJson = JSON.parse(storefrontPreferences)
                enableYRBOPISPDP = customPrefsAsJson.enableYRBOPISPDP
            }
        } catch (e) {
            console.log(e.message)
        }

        useEffect(() => {
            if (!isProductLoading && productOriginal?.id) {
                setProduct(productOriginal)
            }
        }, [productOriginal?.id, isProductLoading, productOriginal?.c_selectedCurrency])

        useEffect(() => {
            onAddToCartDrawerClose()
        }, [location?.pathname])

        useEffect(() => {
            const params = new URLSearchParams(window.location.search)
            const paramsReceipeId = params.get('driveRecipeID')
            if (!paramsReceipeId) {
                setDriveRecipeID(null)
                setEngravingChecksum(null)
            }
        }, [product?.id])

        useEffect(() => {
            if (driveRecipeID || driveRecipeID === '') {
                setEngraveSwatchSpinner(false)
            }
        }, [driveRecipeID])

        const {
            showLoading,
            showInventoryMessage,
            quantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)
        const priceData = useMemo(() => {
            return getPriceData(product, {quantity}, isCart)
        }, [product, quantity])
        let [priceDataSets, setPriceDataSets] = useState(priceData)
        const canAddToWishlist = !isProductLoading
        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const errorContainerRef = useRef(null)
        const productDetailsRef = useRef(null)
        const BNPLMessagesRef = useRef(null)
        const galleryRef = useRef(null)
        const isAfterPayEnabled =
            enableAfterpayPDPCallout && apPDPMpid && apPDPPlacementId && afterpayScriptUrl
        const isAffirmEnabled =
            enableAffirmPDPCallout && affirmPublicKey && affirmMinTotal && affirmScriptUrl
        const isPayPalEnabled = enablePayPalPDPCallout && paypalScriptUrl && paypalMinTotal

        const images = useMemo(() => {
            const exactMatch = findImageGroupBy(product?.imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes: variationParams
            })?.images

            if (exactMatch?.length) {
                return exactMatch
            }

            const fallbackGroup = product?.imageGroups?.find(
                (group) =>
                    group.viewType === LARGE &&
                    group.variationAttributes?.length &&
                    group.images?.length
            )

            return fallbackGroup?.images || []
        }, [product?.imageGroups, variationParams])

        const isGallerySticky = images?.length <= 2
        const imageBadges = useMemo(() => filterImageBadges(product, null), [product])
        const hasCharmBadge = imageBadges?.some(isCharmBadge)
        const xxValueBadge = imageBadges?.find((badge) =>
            badge?.toLowerCase()?.includes('xx-value')
        )
        const isXxValueRight = xxValueBadge?.toLowerCase()?.includes('right')
        // Desktop crumbs overlay the gallery at top/left; left badges sit below them.
        const xxValuePosition = isXxValueRight
            ? {right: '10px', top: '0'}
            : {left: '10px', top: {base: '0', lg: '44px'}}
        const xxValue = product?.c_xxValue || product?.representedProduct?.c_xxValue || ''
        const {disableButton} = useMemo(() => {
            let shouldDisableButton =
                showInventoryMessage ||
                (product?.type?.variant && !variant) ||
                (isAnyChildNotOrderable && !isChildLoading)
            return {disableButton: shouldDisableButton}
        }, [showInventoryMessage, product?.id, isAnyChildNotOrderable, isChildLoading])
        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isSet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)
            if (showError) {
                document.querySelectorAll('.not-selected-attributes').forEach((el) => {
                    if (el?.innerText) {
                        tagManager.purchasePathMessage({
                            productID: product?.id,
                            productName: product?.name,
                            productCategory: product?.c_primaryCategory,
                            messageType: 'specific_error',
                            messageText: el.innerText.trim()
                        })
                    }
                })
            }
            if (scrollToError) {
                window.setTimeout(() => {
                    errorContainerRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    })
                }, 200)
            }

            return hasValidSelection
        }

        const renderAddToCartButton = () => {
            const buttons = []
            const buttonText = {
                disabled: intl.formatMessage({
                    defaultMessage: 'Sold Out',
                    id: 'product_view.button.sold_out'
                }),
                comingSoon: intl.formatMessage({
                    defaultMessage: 'Coming Soon',
                    id: 'product_view.button.comming_soon'
                }),
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Bag',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add All to Bag',
                    id: 'product_view.button.add_all_to_bag'
                }),
                addedToCart: intl.formatMessage({
                    defaultMessage: 'Added to Bag',
                    id: 'product_view.button.added_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart'
                })
            }

            if (geRestricted) {
                return []
            }

            const handleCartItem = async () => {
                const hasValidSelection = isSet ? true : validateAndShowError()

                if (!hasValidSelection || (!isAllProductsSelected && isSet)) {
                    const element =
                        document.querySelector('.color-label') ||
                        document.querySelector('.sf-combo-products')
                    if (element && !isSet) {
                        const offset =
                            element.getBoundingClientRect().top + window.pageYOffset - 250
                        window.scrollTo({
                            top: offset,
                            behavior: 'smooth'
                        })
                    }

                    if (isSet) {
                        toggleShowOptionsMessage(true)

                        window.setTimeout(() => {
                            errorContainerRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            })
                        }, 200)
                    }
                    return null
                }

                if (!addToCart && !updateCart) return null
                const analyticsAttributes = JSON.stringify({
                    listID: 'pdp',
                    listName: 'pdp',
                    itemBadge: getItemBadge(product, {selectedVariant: variant})
                })
                if (!variant) {
                    product.c_analyticsAttributes = analyticsAttributes
                } else {
                    variant.c_analyticsAttributes = analyticsAttributes
                }

                if (updateCart || isQuickAddModal) {
                    setShowAddToCartLoader(true)
                    await yieldToMain()
                    try {
                        await updateCart(variant || product, quantity)
                        setShowAddToCartLoader(false)
                        if (isQuickAddModal) {
                            setShowAddedCta(true)
                            window.setTimeout(() => {
                                setShowAddedCta(false)
                            }, 3000)
                        }
                    } catch (e) {
                        console.error('Error occurred in updateCart', e.message)
                        setShowAddToCartLoader(false)
                    }
                    return
                }
                try {
                    setShowAddToCartLoader(true)
                    const response = isSet
                        ? await addToCart(
                              null,
                              null,
                              null,
                              null,
                              null,
                              false,
                              childProductSelection
                          )
                        : await addToCart(
                              variant,
                              quantity,
                              driveRecipeID ? driveRecipeID : null,
                              driveRecipeID && engravingChecksum ? engravingChecksum : null,
                              driveRecipeID && engravingChecksum ? engravingGtmObject : null
                          )
                    const {productSelectionValues: itemsAdded, basketResponse} = response || {}
                    setShowAddToCartLoader(false)
                    // Open modal only when `addToCart` returns some data
                    // It's possible that the item has been added to cart, but we don't want to open the modal.
                    // See wishlist_primary_action for example.
                    if (itemsAdded) {
                        const promoId = (
                            basketResponse?.bonusDiscountLineItems?.find((i) =>
                                /gwp/i?.test(i.promotionId)
                            ) || basketResponse?.bonusDiscountLineItems?.[0]
                        )?.promotionId // We'll give priority of thw GWP promotions as per the requirement.
                        const cookie =
                            onClient() &&
                            new CookieStorage({
                                keySuffix: '',
                                sharedContext: !onClient()
                            })
                        const gwpPromotionState =
                            onClient() && cookie ? cookie.get(COOKIE_ID) : null
                        if (!(promoId && gwpPromotionState !== 'closed')) {
                            onAddToCartDrawerOpen({
                                product,
                                itemsAdded,
                                selectedQuantity: quantity,
                                priceData: priceData
                            })
                        }
                        setOpenBonusModal(true)
                        setShowAddedCta(true)
                        window.setTimeout(() => {
                            setShowAddedCta(false)
                        }, 3000)
                        scheduleIdleAnalytics(() => {
                            try {
                                monetate.monetateTrackPage(
                                    'addToBagModal',
                                    window.mtBreadcrumb,
                                    basketResponse?.productItems,
                                    customer?.customerType === 'registered',
                                    wishlist?.customerProductListItems?.length || 0,
                                    null,
                                    null,
                                    itemsAdded,
                                    basketResponse?.giftCertificateItems
                                )
                                tagManager.addToCart(
                                    isSet ? product?.setProducts : product,
                                    variant,
                                    isSet ? itemsAdded : null,
                                    null,
                                    'pdp',
                                    null,
                                    false,
                                    driveRecipeID && engravingChecksum ? engravingGtmObject : null,
                                    null,
                                    null,
                                    !!isSet,
                                    analyticsProps
                                )
                                tagManager.addToCartGA4({
                                    itemsAdded,
                                    method: 'pdp',
                                    currency: globaleCurrency,
                                    sorting: undefined,
                                    indexPosition: undefined,
                                    listName: 'pdp',
                                    listId: 'pdp'
                                })
                            } catch (error) {
                                console.error(
                                    'Error occurred in deferred add-to-bag analytics',
                                    error.message
                                )
                            }
                        })
                    }
                } catch (e) {
                    setShowAddToCartLoader(false)
                }
            }

            // child product of bundles do not have Add to Bag button
            if ((addToCart || updateCart) && !isProductPartOfBundle && !geRestricted) {
                const isComingSoon = product?.c_comingSoonProduct
                buttons.push(
                    <Button
                        key="cart-button"
                        className={
                            !product?.inventory?.orderable
                                ? 'pdp-cart-button attn-oos'
                                : 'pdp-cart-button'
                        }
                        onClick={handleCartItem}
                        isDisabled={disableButton || isComingSoon || limitReached}
                        isLoading={
                            isBasketLoading ||
                            showAddToCartLoader ||
                            isProductLoading ||
                            isChildLoading
                        }
                        width="100%"
                        variant="solid"
                        brand={isYR ? 'yr' : 'ks'}
                        {...styles.cartButton}
                    >
                        {showAddedCta && (
                            <Image
                                src={getUrl('UIImages/images/tick-circle-white.svg')}
                                className="tick-icon"
                                height="14px"
                                width="14px"
                                marginRight="5px"
                                alt="Added to Bag"
                            />
                        )}

                        {disableButton && !isComingSoon
                            ? buttonText.disabled
                            : isComingSoon
                            ? buttonText.comingSoon
                            : showAddedCta
                            ? buttonText.addedToCart
                            : updateCart && !isQuickAddModal
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : buttonText.addToCart}
                    </Button>
                )
            }

            return buttons
        }

        const renderGERestrictedText = ({colorVariant = 'black'}) => {
            return (
                <Box
                    textAlign="center"
                    className="pdp-wishlist-btn"
                    borderColor={colorVariant === 'black' ? 'black' : 'white'}
                    borderWidth="1px"
                    borderStyle="solid"
                    padding="14px 3%"
                    color={colorVariant === 'black' ? 'black' : 'white'}
                    display="inline-flex"
                >
                    {intl.formatMessage({
                        defaultMessage:
                            'This Product Is Not Eligible For Shipment Outside Of The USA.',
                        id: 'product_view.button.ge_forbidden'
                    })}
                </Box>
            )
        }

        const renderWishlistButton = () => {
            const buttons = []
            const buttonText = {
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                addSetToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Set to Wishlist',
                    id: 'product_view.button.add_set_to_wishlist'
                }),
                addBundleToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Wishlist',
                    id: 'product_view.button.add_bundle_to_wishlist'
                })
            }

            const handleWishlistItem = async () => {
                // Since we always keep the swatch (size/color) selected, we don't need any pre-conditions here
                if (!updateWishlist && !addToWishlist) return null
                if (updateWishlist) {
                    updateWishlist(product, variant, quantity)
                    return
                }
                addToWishlist(product, variant, quantity)
            }

            // child product of bundles do not have add to wishlist button
            if ((addToWishlist || updateWishlist) && !isProductPartOfBundle && !isSet) {
                const label = updateWishlist
                    ? buttonText.update
                    : isProductASet
                    ? buttonText.addSetToWishlist
                    : isProductABundle
                    ? buttonText.addBundleToWishlist
                    : buttonText.addToWishlist

                buttons.push(
                    <Box key={label} className="wishlist-button">
                        <ButtonWithRegistration
                            key="wishlist-button"
                            onClick={handleWishlistItem}
                            disabled={showPriceSkeleton || !canAddToWishlist}
                            display={showPriceSkeleton || countryISO !== 'US' ? 'none' : 'flex'}
                            width="40px"
                            height="40px"
                            productid={product?.id}
                            productname={product?.name}
                            category={product?.c_primaryCategory || ''}
                            wishlistitems={wishlist?.customerProductListItems?.length || 0}
                            ispdp={ispdp}
                            variant="unstyled"
                            background="gray.50"
                            _focusVisible={{
                                boxShadow: 'none'
                            }}
                            _hover={{
                                background: 'gray.400'
                            }}
                            borderRadius="50%"
                            alignItems="center"
                            justifyContent="center"
                            aria-label={label}
                        >
                            {isItemInWishlist ? (
                                <>
                                    {productAddedToWishlist && <WishlistAnimation />}
                                    <Image
                                        width="15px"
                                        height="15px"
                                        alt="filled heart"
                                        src={getUrl('UIImages/images/fill-heart-icon.svg')}
                                    />
                                </>
                            ) : (
                                <Image
                                    src={getUrl('UIImages/images/empty-heart-icon.svg')}
                                    width="15px"
                                    height="15px"
                                    alt="empty heart"
                                />
                            )}
                        </ButtonWithRegistration>
                    </Box>
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (isSet) {
                toggleShowOptionsMessage(false)
            }
        }, [childProductSelection])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        const ispdp = !isProductPartOfSet
        const productVariationAttributes =
            isProductPartOfSet || !product?.c_PDPSwatches
                ? variationAttributes
                : [
                      ...Object.entries(JSON.parse(product?.c_PDPSwatches)).map(
                          ([category, ids]) => {
                              const originalSelected = variationAttributes[0].selectedValue
                              return {
                                  id: category.toLowerCase().replace(/\s+/g, '-'),
                                  originalId: variationAttributes[0].id,
                                  name: category,
                                  values: variationAttributes[0].values?.filter(
                                      (v) =>
                                          ids.length
                                              ? ids.includes(v.value) // Keep only values belonging to this category
                                              : !Object.values(JSON.parse(product?.c_PDPSwatches))
                                                    .flat()
                                                    .includes(v.value) // Exclude those already categorized
                                  ),
                                  selectedValue: originalSelected
                              }
                          }
                      ),
                      ...variationAttributes.filter((attr) => attr.name !== 'Color')
                  ]
        const sizeGuideRef = useRef()
        const onOpenSizeGuide = useCallback(() => sizeGuideRef.current?.open(), [])

        const isOrderable = product?.inventory?.orderable
        const hasBackInStockForm = product?.c_BackInStockForm
        const renderBackInStockForm =
            (!isOrderable && hasBackInStockForm) ||
            (!isOrderable && product?.c_comingSoonProduct && product?.c_BackInStockForm)
        const renderAddToBag = !renderBackInStockForm
        const geRestricted =
            countryISO &&
            countryISO !== 'US' &&
            (product?.c_geIsForbidden || product?.c_geRestrictedCountries?.indexOf(countryISO) > -1)
        const [showIAdvizeStarters, setShowIAdvizeStarters] = useState(false)
        useEffect(() => {
            /** Not a reactJS practice - If IAdvize starters are rendered, we'll show its parent element **/
            const observer = new MutationObserver(() => {
                const target = document.querySelector('#embedded-conversation-starter-container')
                if (target) {
                    // Prevent duplicate bindings
                    setShowIAdvizeStarters(true)
                }
            })

            observer.observe(document.body, {
                childList: true,
                subtree: true
            })

            return () => observer.disconnect()
        }, [])

        const promoIds = product?.productPromotions?.map((promotion) => promotion.promotionId) || []
        const enabledPromotion = promoIds.length > 0 && !isServer
        const {data: promotionsDetails, isLoading: isPromoLoading} = usePromotions(
            {
                parameters: {
                    ids: promoIds.join(',')
                }
            },
            {
                enabled: enabledPromotion
            }
        )
        const promotions = promotionsDetails?.data || []
        const color = promotions?.find(
            (p) => p?.c_type === 'PRODUCT' && p?.c_textColor
        )?.c_textColor

        useEffect(() => {
            setTimeout(() => {
                setShowPriceSkeleton(
                    !globaleCurrency || globaleCurrency != product?.c_selectedCurrency
                )
            }, 300)
        }, [globaleCurrency, product?.c_selectedCurrency])

        //set total price for product sets
        useEffect(() => {
            if (product?.type?.set) {
                setShowPriceSkeleton(true)

                let currentSetPriceData = getSetPriceData(
                    product?.setProducts,
                    childProductSelection
                )

                if (currentSetPriceData?.currentPrice !== 0) {
                    priceDataSets = {...priceDataSets, ...currentSetPriceData}

                    setPriceDataSets(priceDataSets)
                }

                setTimeout(() => {
                    setShowPriceSkeleton(false)
                }, 300)
            }
        }, [childProductSelection, product?.setProducts, globaleCurrency])

        const showFinalSaleMsg = Boolean(
            product &&
                (!product.c_isReturnable ||
                    product.c_isEngravable ||
                    product.c_isExcludedFromSiteWidePromotions) &&
                (!product.c_isReturnable || product.c_isEngravable)
        )
        useEffect(() => {
            const purchasePathSchedule = scheduleIdleAnalytics(() => {
                productVariationAttributes
                    ?.filter((attr) => !attr.selectedValue?.name)
                    ?.forEach(({id}) => {
                        const attributeId =
                            id === 'carat' || id === 'size'
                                ? 'size'
                                : id?.toLowerCase()?.indexOf('color') !== -1
                                ? 'color'
                                : id
                        tagManager.purchasePathMessage({
                            productID: product?.id,
                            productName: product?.name,
                            productCategory: product?.c_primaryCategory,
                            messageType: 'product_specific',
                            messageText: `Select a ${attributeId}`
                        })
                    })
            })

            return () => purchasePathSchedule?.cancel?.()
        }, [product?.id])
        useEffect(() => {
            const productMessageSchedule = scheduleIdleAnalytics(() => {
                const msgs = []

                if (showFinalSaleMsg) {
                    msgs.push(
                        intl.formatMessage({
                            id: 'final.sales',
                            defaultMessage: 'Final Sale. No returns or exchanges.'
                        })
                    )
                }

                if (product?.c_isExcludedFromSiteWidePromotions) {
                    msgs.push(
                        intl.formatMessage({
                            id: 'excluded.from.promo',
                            defaultMessage: 'Excluded from sitewide promotions'
                        })
                    )
                }

                if (product?.c_disclaimerText) {
                    msgs.push(product.c_disclaimerText)
                }

                msgs.forEach((msg) => {
                    tagManager.purchasePathMessage({
                        productID: product?.id,
                        productName: product?.name,
                        productCategory: product?.c_primaryCategory,
                        messageType: 'product_specific',
                        messageText: msg
                    })
                })
            })

            return () => productMessageSchedule?.cancel?.()
        }, [
            showFinalSaleMsg,
            product?.c_isExcludedFromSiteWidePromotions,
            product?.c_disclaimerText,
            product?.id,
            product?.name,
            product?.c_primaryCategory
        ])
        const isPreOrder = !geRestricted && enablePurpleDot && product?.inventory?.preorderable
        const isEditEngraving = location?.search?.indexOf('driveRecipeID') !== -1
        const [desktopGalleryEngImages, setDesktopGalleryEngImages] = useState(null)

        const meterPromoDetails = promotions?.find(function (data) {
            return (
                (data?.id?.indexOf('GWP') > -1 ||
                    data?.id?.indexOf('PWP') > -1 ||
                    data?.id?.indexOf('BMSM') > -1) &&
                !!data?.c_enableProgressMeter &&
                data?.c_bmsmAndGwpMeterConfig
            )
        })

        const promoMeterType =
            meterPromoDetails?.id?.indexOf('GWP') > -1 || meterPromoDetails?.id?.indexOf('PWP') > -1
                ? 'GWP'
                : meterPromoDetails?.id?.indexOf('BMSM') > -1
                ? 'BMSM'
                : null

        const basketProductIds = useMemo(() => getBasketProductIds(basket), [basket?.productItems])
        const shouldFetchBasketProducts =
            !isServer &&
            promoMeterType === 'GWP' &&
            Boolean(meterPromoDetails?.id && basketProductIds.length)
        const {data: basketProductsData, isLoading: isQualifyingProductsLoading} = useProducts(
            {
                parameters: {
                    ids: basketProductIds.join(','),
                    expand: ['promotions'],
                    c_selectedCountry: countryISO,
                    c_selectedCurrency: currencyCode
                }
            },
            {
                enabled: shouldFetchBasketProducts
            }
        )
        const gwpQualifyingProductIds = useMemo(
            () => getQualifyingProductIds(basketProductsData?.data, meterPromoDetails?.id),
            [basketProductsData?.data, meterPromoDetails?.id]
        )

        return (
            <Flex
                data-testid="product-view"
                ref={ref}
                direction={'column'}
                width="full"
                backgroundColor="gray.100"
            >
                {(showAddToCartLoader || engraveSwatchSpinner) && (
                    <LoadingSpinner position="fixed" top="auto" />
                )}
                {!isCart && ispdp && <BreadcrumbScemaMarkup product={product} />}
                {/* Basic information etc. title, price, breadcrumb*/}

                {!isCart && <SizeGuide ref={sizeGuideRef} sizeChart={product?.c_sizeChart} />}

                <Flex
                    direction={{base: 'column', md: isCart ? 'row' : 'column', lg: 'row'}}
                    width="full"
                    alignItems="start"
                    backgroundColor={isSet ? 'white' : 'gray.100'}
                    className="pdp-image-gallery-flex"
                    marginTop={{base: isCart ? '30px' : '0px', md: '0px'}}
                >
                    <Box
                        ref={galleryRef}
                        className="pdp-image-gallery"
                        width={isCart ? '-webkit-fill-available' : 'unset'}
                        flex={1}
                        position={{base: 'relative', lg: isGallerySticky ? 'sticky' : 'relative'}}
                        top={{lg: isGallerySticky ? '74px' : '0'}}
                        backgroundColor="gray.100"
                    >
                        {!isCart && product?.id && (
                            <MirelzButtons
                                productId={product?.id}
                                productName={product?.name}
                                basket={basket}
                                preorderable={product?.inventory?.preorderable}
                                isAddToBagEnabled={isOrderable}
                                mobileImageIndex={mobileImageIndex}
                            />
                        )}
                        <Box className="image-gallery-box" position="relative">
                            {!hasCharmBadge && xxValue && xxValueBadge && ispdp && !isCart && (
                                <Box
                                    position="absolute"
                                    zIndex="5"
                                    border="none"
                                    className="pdp-xx-badge"
                                    backgroundColor="white"
                                    padding="8px 10px"
                                    {...xxValuePosition}
                                >
                                    <XxValueBadgeDisplay
                                        xxValue={xxValue}
                                        badge={xxValueBadge}
                                        variant="pdp"
                                    />
                                </Box>
                            )}
                            {product ? (
                                <ImageGallery
                                    product={product}
                                    enableEngraving={enableEngraving}
                                    size={imageSize}
                                    desktopGalleryEngImages={
                                        driveRecipeID ? desktopGalleryEngImages : null
                                    }
                                    openEngraving={openEngraving}
                                    setOpenEngraving={() => {
                                        setOpenEngraving(true)
                                    }}
                                    slugUrl={product?.slugUrl}
                                    imageGroups={product?.imageGroups}
                                    productName={product?.name}
                                    isCart={isCart}
                                    setMobileImageIndex={setMobileImageIndex}
                                    selectedVariationAttributes={variationParams}
                                    isEngagementProduct={isEngagementProduct}
                                    lazy={isProductPartOfSet || isProductPartOfBundle}
                                />
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                        {!isCart && (
                            <Box
                                display={{base: 'none', lg: 'block'}}
                                position="absolute"
                                top="12px"
                                left="40px"
                                sx={{
                                    '.sf-breadcrumb': {
                                        margin: 0
                                    }
                                }}
                            >
                                <Breadcrumb
                                    categories={product?.c_breadcrumbs}
                                    isProductLoading={isProductLoading}
                                    ispdp={ispdp}
                                />
                            </Box>
                        )}
                    </Box>

                    {!isCart && (
                        <Box
                            display={{base: 'block', lg: 'none'}}
                            sx={{
                                '.sf-breadcrumb': {
                                    marginBottom: {base: isSet ? '10px' : '15px', lg: '30px'}
                                }
                            }}
                            width="100%"
                            backgroundColor={isSet ? 'white' : 'gray.100'}
                        >
                            <Breadcrumb
                                categories={product?.c_breadcrumbs}
                                isProductLoading={isProductLoading}
                                ispdp={ispdp}
                            />
                        </Box>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack
                        ref={productDetailsRef}
                        className="pdp-product-details"
                        paddingX={{base: '15px', lg: '60px'}}
                        align="stretch"
                        maxWidth={{lg: '500px'}}
                        position={{lg: isGallerySticky ? 'static' : 'sticky'}}
                        top={{lg: '74px'}}
                        paddingTop={{lg: isSet ? '62px' : '30px'}}
                        backgroundColor={isSet ? 'white' : 'gray.100'}
                        alignSelf={{lg: 'flex-start'}}
                        spacing="20px"
                        flex={1}
                        height={product?.type?.set ? 'fit-content' : 'unset'}
                    >
                        <ProductViewHeader
                            name={product?.name}
                            product={product}
                            variant={variant}
                            countryISO={countryISO}
                            showPriceSkeleton={showPriceSkeleton}
                            priceData={priceData}
                            priceDataSets={priceDataSets}
                            globalePrices={globalePrices}
                            pwpTargetPromotionId={pwpTargetPromotionId}
                            isCart={isCart}
                            currency={product?.currency || activeCurrency}
                            isProductPartOfBundle={isProductPartOfBundle}
                            wishlist={renderWishlistButton}
                            productDetailsRef={productDetailsRef}
                            BNPLMessagesRef={BNPLMessagesRef}
                            galleryRef={galleryRef}
                            color={color}
                            isEngagementProduct={isEngagementProduct}
                            promotions={promotions}
                            selectedCarat={selectedCarat}
                        />

                        {!isCart &&
                            !isEngagementProduct &&
                            (product?.productPromotions || product?.c_promotions) && (
                                <ProductPromotionCallouts product={product} />
                            )}

                        <VStack
                            align="stretch"
                            spacing={'30px'}
                            className="swatches-stack"
                            display={isSet ? 'none' : 'flex'}
                        >
                            {!isCart && product?.c_isEngravable && enableEngraving ? (
                                <Box
                                    className="engravable-wrapper"
                                    {...styles.engravingWrapper.main}
                                    display={variant ? 'block' : 'none'}
                                >
                                    <Engravable
                                        product={product}
                                        addToCart={addToCart}
                                        customer={customer}
                                        disableAddToBag={disableButton}
                                        setEngravingGtmObject={setEngravingGtmObject}
                                        basket={basket}
                                        priceData={priceData}
                                        setDesktopGalleryEngImages={setDesktopGalleryEngImages}
                                        wishlist={wishlist}
                                        variant={variant}
                                        isEditEngraving={isEditEngraving}
                                        openEngraving={openEngraving}
                                        setOpenEngraving={(bool) => {
                                            setOpenEngraving(bool)
                                        }}
                                        setDriveRecipeID={setDriveRecipeID}
                                        driveRecipeID={driveRecipeID}
                                        setEngravingChecksum={setEngravingChecksum}
                                    />
                                </Box>
                            ) : (
                                <></>
                            )}
                            {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                            <ProductViewSwatches
                                product={product}
                                showLoading={showLoading}
                                productVariationAttributes={productVariationAttributes}
                                setSelectedCarat={(e) => {
                                    setSelectedCarat(e)
                                }}
                                isEngagementProduct={isEngagementProduct}
                                setProduct={(e) => {
                                    setProduct(e)
                                }}
                                setEngraveSwatchSpinner={(e) => {
                                    setEngraveSwatchSpinner(e)
                                }}
                                styles={styles}
                                showOptionsMessage={showOptionsMessage}
                                onOpenSizeGuide={onOpenSizeGuide}
                                ispdp={ispdp}
                                isEditEngraving={isEditEngraving}
                                isProductPartOfSet={isProductPartOfSet}
                                driveRecipeID={driveRecipeID}
                                variant={variant}
                                selectedCarat={selectedCarat}
                                isCart={isCart}
                                setShouldFetchProduct={(e) => {
                                    setShouldFetchProduct(e)
                                }}
                                suppressSwatchSpinner={suppressSwatchSpinner}
                            />

                            {!isCart && isProductASet && <p>{product?.shortDescription}</p>}
                            {!isCart &&
                                !isEngagementProduct &&
                                !isServer &&
                                !product?.c_hideGwpBmsmMeter &&
                                promoMeterType === 'GWP' && (
                                    <GWPMeter
                                        basket={basket}
                                        isBasketLoading={isBasketLoading}
                                        product={product}
                                        dataLayerLoaded={dataLayerLoaded}
                                        size="pdp"
                                        promoDetail={meterPromoDetails}
                                        isPromoLoading={isPromoLoading}
                                        qualifyingProductIds={gwpQualifyingProductIds}
                                        isQualifyingProductsLoading={isQualifyingProductsLoading}
                                    />
                                )}
                            {!isCart &&
                                !isEngagementProduct &&
                                !isServer &&
                                !product?.c_hideGwpBmsmMeter &&
                                promoMeterType === 'BMSM' && (
                                    <BMSMMeter
                                        size="pdp"
                                        product={product}
                                        dataLayerLoaded={dataLayerLoaded}
                                        promoDetail={meterPromoDetails}
                                        isPromoLoading={isPromoLoading}
                                    />
                                )}
                            {!isCart &&
                                !isEngagementProduct &&
                                countryISO === 'US' &&
                                product?.c_availableForInStorePickup &&
                                (isKS || (isYR && enableYRBOPISPDP)) && (
                                    <StoreAvailability product={product} />
                                )}
                        </VStack>
                        {isSet && (
                            <Box>
                                <ComboChildProducts
                                    comboProduct={comboProduct}
                                    basket={basket}
                                    showAddedCta={showAddedCta}
                                    parentProduct={product}
                                    setIsChildLoading={setIsChildLoading}
                                    addToCart={addToCart}
                                    isBasketLoading={isBasketLoading}
                                    wishlist={wishlist}
                                    customer={customer}
                                    isProductLoading={isProductLoading}
                                    setChildProductSelection={setChildProductSelection}
                                    childProductSelection={childProductSelection}
                                    countryISO={countryISO}
                                />
                            </Box>
                        )}
                        <Box
                            ref={errorContainerRef}
                            className="select-all-options"
                            display={showOptionsMessage && isSet ? 'block' : 'none'}
                        >
                            {showOptionsMessage && isSet && (
                                <Text
                                    color="red.400"
                                    fontSize="14px"
                                    letterSpacing="0.07px"
                                    fontWeight="400"
                                    lineHeight="22px"
                                >
                                    {intl.formatMessage({
                                        defaultMessage: 'Please select all your options above'
                                    })}
                                </Text>
                            )}
                        </Box>
                        {!isCart && isEngagementProduct && (
                            <DiamondExpertSection product={product} />
                        )}
                        {!isQuickAddModal && isCart && product?.inventory?.stockLevel && (
                            <Box
                                {...styles.actionWrapper}
                                className="quantity-wrapper"
                                sx={{
                                    '.chakra-select__wrapper': {
                                        maxWidth: '100%',
                                        maxHeight: 'fit-content',
                                        width: '100%'
                                    },
                                    select: {
                                        textAlign: 'left'
                                    }
                                }}
                            >
                                <Box gap="15px" display="inline-flex" marginBottom="20px">
                                    <Text {...styles.variationsStack.labelBox}>
                                        <FormattedMessage
                                            id="product_view.label.quantity"
                                            defaultMessage="Quantity"
                                        />
                                    </Text>
                                    <Text {...styles.variationsStack.labelBox} fontWeight="300">
                                        {quantity}
                                    </Text>
                                </Box>
                                <QuantityPicker
                                    id="quantity"
                                    step={1}
                                    value={quantity}
                                    min={1}
                                    max={
                                        product?.c_orderLimit ||
                                        (product?.inventory?.stockLevel > 12
                                            ? 12
                                            : product?.inventory?.stockLevel)
                                    }
                                    onChange={(stringValue, numberValue) => {
                                        // Set the Quantity of product to value of input if value number
                                        if (numberValue >= 0) {
                                            setQuantity(numberValue)
                                        } else if (stringValue === '') {
                                            // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                            // User will not be able to add '' quantity to the cart due to the add to cart button enablement rules
                                            setQuantity(stringValue)
                                        }
                                    }}
                                    onBlur={(e) => {
                                        // Default to 1the `minOrderQuantity` if a user leaves the box with an invalid value
                                        const value = e.target.value
                                        if (parseInt(value) < 0 || value === '') {
                                            setQuantity(1)
                                        }
                                    }}
                                    onFocus={(e) => {
                                        // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
                                        // new quantity with one click. NOTE: This is something that can be refactored into the parent
                                        // component, potentially as a prop called `selectInputOnFocus`.
                                        e.target.select()
                                    }}
                                    productName={product?.name}
                                />
                            </Box>
                        )}
                        {!isEngagementProduct && (
                            <Box>
                                {renderAddToBag &&
                                    !geRestricted &&
                                    !isEditEngraving &&
                                    !isQuickAddModal &&
                                    !(enablePurpleDot && product?.inventory?.preorderable) && (
                                        <Box className="add-to-cart-wrapper">
                                            {renderAddToCartButton()}
                                            {isQuickAddModal && (
                                                <Box
                                                    {...formattedURL(product?.slugUrl)}
                                                    {...styles.viewDetailsLink}
                                                    onClick={onModalClose}
                                                >
                                                    <FormattedMessage
                                                        id="quick.add.view_details"
                                                        defaultMessage="View Details"
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                {!isCart && geRestricted && (
                                    <Box className="ge-restricted">
                                        {renderGERestrictedText({colorVariant: 'black'})}
                                    </Box>
                                )}
                                {!isCart && !geRestricted && limitReached && (
                                    <QuantityLimit product={product} />
                                )}

                                {!isCart &&
                                    !geRestricted &&
                                    enablePurpleDot &&
                                    product?.inventory?.preorderable && (
                                        <PDButton
                                            product={product}
                                            variant={variant}
                                            currency={activeCurrency}
                                        />
                                    )}

                                {!isCart &&
                                    !geRestricted &&
                                    renderBackInStockForm &&
                                    (useAttentiveBackInStock ? (
                                        <AttentiveBackInStock product={product} variant={variant} />
                                    ) : (
                                        <ProductNotifyForm product={product} />
                                    ))}
                                {!isCart && product?.c_isEngravable && enableEngraving && (
                                    <Button
                                        onClick={() => {
                                            setOpenEngraving(true)
                                            tagManager.openEngravingModal()
                                        }}
                                        {...styles?.[
                                            isEditEngraving
                                                ? 'EditEngravingButton'
                                                : 'AddEngravingButton'
                                        ].main}
                                        display={variant ? 'inline-flex' : 'none'}
                                    >
                                        {!isEditEngraving && (
                                            <Image
                                                width="14.5px"
                                                height="14px"
                                                alt="Add Complimentary Engraving"
                                                src={getUrl(
                                                    'UIImages/images/add-complimentary-engraving.svg'
                                                )}
                                            />
                                        )}
                                        <Text
                                            {...styles?.[
                                                isEditEngraving
                                                    ? 'EditEngravingButton'
                                                    : 'AddEngravingButton'
                                            ].text}
                                        >
                                            {!isEditEngraving ? (
                                                <FormattedMessage
                                                    id="add.complimentary.engraving"
                                                    defaultMessage="Add Complimentary Engraving"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id="update.engraving"
                                                    defaultMessage="Update Engraving"
                                                />
                                            )}
                                        </Text>
                                    </Button>
                                )}
                                <Box className="sticky-bar-reference"></Box>
                            </Box>
                        )}

                        {!isCart &&
                            showFinalSaleMsg &&
                            !isEditEngraving &&
                            !isEngagementProduct && (
                                <Box marginTop="-20px">
                                    <Text
                                        color="black.100"
                                        fontWeight="400"
                                        fontSize="14px"
                                        textAlign="center"
                                        lineHeight="22px"
                                    >
                                        <FormattedMessage
                                            id="final.sales"
                                            defaultMessage="Final Sale. No returns or exchanges."
                                        />
                                    </Text>
                                </Box>
                            )}

                        {!isCart &&
                            product?.c_isExcludedFromSiteWidePromotions &&
                            !isEditEngraving &&
                            !isEngagementProduct && (
                                <Box marginTop="-20px">
                                    <Text
                                        color="black.100"
                                        fontWeight="400"
                                        fontSize="14px"
                                        textAlign="center"
                                        lineHeight="22px"
                                    >
                                        <FormattedMessage
                                            id="excluded.from.promo"
                                            defaultMessage="Excluded from sitewide promotions"
                                        />
                                    </Text>
                                </Box>
                            )}
                        {!isCart && product?.c_disclaimerText && (
                            <Text {...styles.disclaimerText}>{product?.c_disclaimerText}</Text>
                        )}
                        {!isCart && !isEngagementProduct && product?.c_welInstituteContentAsset && (
                            <Box
                                className="wel-institute-asset-wrapper"
                                dangerouslySetInnerHTML={{
                                    __html: product?.c_welInstituteContentAsset
                                }}
                                {...styles.welInstitute}
                            />
                        )}
                        {!isCart && !isEngagementProduct && (
                            <Box
                                id="iadvize-conversation-starters-pdp"
                                display={showIAdvizeStarters ? 'block' : 'none'}
                                backgroundColor="white"
                                borderRadius="10px"
                                border="1px solid 1px solid var(--chakra-colors-gray-400)"
                                sx={{
                                    '#embedded-conversation-starter-container': {
                                        margin: '0 !important',
                                        padding: '20px'
                                    }
                                }}
                            ></Box>
                        )}
                        {!isCart &&
                            !isEngagementProduct &&
                            (product?.c_pdpReturnsMessage ||
                                product?.c_pdpGiftMessage ||
                                (product?.c_isCustomizeBadgeActive && product?.c_colorBarAsset) ||
                                product?.c_pdpShippingMessage) && <Callout product={product} />}
                        {!isCart &&
                            !isEngagementProduct &&
                            variant?.price &&
                            variant?.productId &&
                            countryISO === 'US' &&
                            (isAffirmEnabled || isAfterPayEnabled || isPayPalEnabled) && (
                                <Box ref={BNPLMessagesRef} className="bnpl-wrapper">
                                    <BNPLMessages
                                        variant={product?.type?.set ? product : variant}
                                        product={product}
                                        price={
                                            priceData?.currentPrice ||
                                            variant?.price | product?.price
                                        }
                                        customer={customer}
                                        isPreOrder={isPreOrder}
                                    />
                                </Box>
                            )}

                        {!isCart &&
                            product?.c_pdpAccordionConfig &&
                            (isHydrated() ? (
                                <ProductDetailsAccordion
                                    product={product}
                                    galleryRef={galleryRef}
                                    isSet={isSet}
                                    productDetailsRef={productDetailsRef}
                                    isEngagementProduct={isEngagementProduct}
                                    onOpenSizeGuide={onOpenSizeGuide}
                                />
                            ) : (
                                <>
                                    <Skeleton padding="20px 0" height="31px" width="100%" />
                                    <Skeleton padding="20px 0" height="31px" width="100%" />
                                    <Skeleton padding="20px 0" height="31px" width="100%" />
                                    <Skeleton padding="20px 0" height="31px" width="100%" />
                                </>
                            ))}
                        {!isCart && isEngagementProduct && product?.c_welInstituteContentAsset && (
                            <Box
                                className="wel-institute-asset-wrapper"
                                dangerouslySetInnerHTML={{
                                    __html: product?.c_welInstituteContentAsset
                                }}
                                {...styles.welInstitute}
                            />
                        )}
                        {!isCart && (
                            <Box
                                id="pdp-monetate-ctl"
                                className="pdp-monetate-ctl"
                                sx={{'&:empty': {display: 'none'}}}
                            />
                        )}
                        {!isCart &&
                            variant?.productId &&
                            product?.c_showCompleteTheLook &&
                            product?.c_completeTheLookIDs?.length && (
                                <Box>
                                    <CompleteTheLook
                                        completeTheLookIDs={product?.c_completeTheLookIDs}
                                        variantId={variant?.productId}
                                        breadcrumbs={product?.c_breadcrumbs}
                                        completeTheLookMessage={product?.c_completeTheLookMessage}
                                    />
                                </Box>
                            )}
                    </VStack>
                    {isCart && isQuickAddModal && (
                        <Box className="bottom-details-wrapper">
                            {renderAddToBag &&
                                !geRestricted &&
                                !isEditEngraving &&
                                !(enablePurpleDot && product?.inventory?.preorderable) && (
                                    <Box className="add-to-cart-wrapper">
                                        {renderAddToCartButton()}
                                    </Box>
                                )}
                        </Box>
                    )}
                </Flex>

                {!isCart && (
                    <Box display="block" className="sticky-bar-wrapper">
                        {ispdp && !geRestricted && !isPreOrder && (
                            <StickyBar
                                priceData={priceData}
                                priceDataSets={priceDataSets}
                                isSet={isSet}
                                variationAttributes={variationAttributes}
                                renderButton={renderAddToCartButton}
                                product={product}
                                variant={variant}
                                imageGroups={product?.imageGroups}
                                selectedVariationAttributes={variationParams}
                            />
                        )}
                    </Box>
                )}
            </Flex>
        )
    }
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    wishlist: PropTypes.object,
    childOfBundleQuantity: PropTypes.number,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    isItemInWishlist: PropTypes.bool,
    productAddedToWishlist: PropTypes.bool,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    isEngagementProduct: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    onVariantSelected: PropTypes.func,
    countryISO: PropTypes.any,
    isSet: PropTypes.bool,
    comboProduct: PropTypes.any,
    dataLayerLoaded: PropTypes.bool,
    basket: PropTypes.any,
    validateOrderability: PropTypes.func,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    isCart: PropTypes.bool,
    isQuickAddModal: PropTypes.bool,
    pwpTargetPromotionId: PropTypes.string,
    onModalClose: PropTypes.func,
    analyticsProps: PropTypes.object,
    suppressSwatchSpinner: PropTypes.bool,
    globalePrices: PropTypes.array
}

export default ProductView
